// The platform status view (`SC-F907`): `GET /v1/status`.
//
// It is the only module in `api/` that reads an ANONYMOUS route, and that is the
// whole point of the feature rather than an accident of implementation. A status
// page a customer cannot reach while signed out is unavailable during a sign-in
// outage — which is one of the outages it exists for — so every call here passes
// `anonymous: true`, and a stale stored token can never turn the banner into a 401.
//
// It is read by the app shell on every route change, so the banner appears on every
// page while an incident is open. There is deliberately no page of its own: the SPA's
// route table has no `/status` entry, and the disclosure inside the banner carries the
// same content a page would. See `main.ts`.

import { getJSON } from "./http.js";

/**
 * The composed status. `operational` is the absence of a severity;
 * `unknown` is **not** operational — it is the server saying it could not read its
 * own incident record, and the distinction is the single most important thing this
 * endpoint gets right. Treating them alike would render "everything is fine" from
 * "we could not check".
 */
export type PlatformStatusLevel =
  | "operational"
  | "maintenance"
  | "degraded"
  | "outage"
  | "unknown";

/** One incident notice — prose an operator wrote. */
export interface Incident {
  id: string;
  title: string;
  body: string;
  severity: "maintenance" | "degraded" | "outage";
  /** The shared failure domain (`02` §12), or `control_plane` / `other`. */
  domain: string;
  state: "open" | "resolved";
  started_at: string;
  /** Null while the incident is open. */
  resolved_at: string | null;
  updated_at: string;
}

/**
 * One derived signal: something the control plane observed about itself with nobody
 * authoring a notice. `derived` is always true and is rendered as such — a reader has
 * to be able to tell an inference from a statement.
 */
export interface StatusSignal {
  id: string;
  severity: "maintenance" | "degraded" | "outage";
  domain: string;
  summary: string;
  derived: boolean;
}

/** One entry of `02` §12's shared-failure-domain table. */
export interface FailureDomain {
  domain: string;
  blast_radius: string;
  /** False for the control plane and the Swarm quorum: nothing in the query path
   * touches either, which is the most reassuring true thing we can say during our
   * own outage. */
  affects_query_path: boolean;
}

export interface PlatformStatus {
  status: PlatformStatusLevel;
  as_of: string;
  incidents: Incident[];
  /** Notices resolved within the last week. */
  recent: Incident[];
  derived: StatusSignal[];
  shared_failure_domains: FailureDomain[];
  control_plane: { version: string; replicas: number };
  /** Honest statements about what this answer is and is not. Rendered verbatim. */
  notes: string[];
}

/** getStatus reads the platform status. Anonymous; never sends a credential. */
export function getStatus(): Promise<PlatformStatus> {
  return getJSON<PlatformStatus>("/v1/status", { anonymous: true });
}

/**
 * isQuiet reports whether the status needs no banner at all.
 *
 * `unknown` is deliberately NOT quiet: the server could not confirm that nothing is
 * wrong, and the banner says so. That is the one line of code in the SPA where
 * `SC-F907`'s promise — nobody is told nothing while something is wrong — either holds
 * or does not.
 */
export function isQuiet(status: PlatformStatus | undefined): boolean {
  return status === undefined || status.status === "operational";
}
